import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import lotService from "../../services/lotService";
import stockCategoryService from "../../services/stockCategoryService";
import stockProcessingService from "../../services/stockProcessingService";
import alertService from "../../utils/alertService";
import ErrorAlert from "../../components/common/ErrorAlert";
import LotCreateModal from "../Modals/LotCreateModal";

function StockTransferCreatePage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [lotOptions, setLotOptions] = useState([]);
  const [stockCategoryOptions, setStockCategoryOptions] = useState([]);
  const [fromLotwiseStock, setFromLotwiseStock] = useState([]);
  const [isLoadingStock, setIsLoadingStock] = useState(false);
  const [isLotModalOpen, setIsLotModalOpen] = useState(false);

  const [form, setForm] = useState({
    fromLotId: "",
    toLotId: "",
    fromStockCategoryId: "",
    toStockCategoryId: "",
    transferWeight: "",
  });

  const loadLots = useCallback(async () => {
    try {
      const response = await lotService.getActiveLots({ showLocked: false });
      setLotOptions(
        (response?.items || []).map((l) => ({
          label: l.lotNo,
          value: String(l.lotId),
        })),
      );
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadStockCategories = useCallback(async () => {
    try {
      const response = await stockCategoryService.getActiveStockCategories();
      setStockCategoryOptions(
        (response?.items || []).map((c) => ({
          label: c.stockCategoryName,
          value: String(c.stockCategoryId),
        })),
      );
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadLots();
    loadStockCategories();
  }, [loadLots, loadStockCategories]);

  // Load stock for the source lot
  useEffect(() => {
    if (!form.fromLotId) {
      setFromLotwiseStock([]);
      setForm((prev) => ({
        ...prev,
        fromStockCategoryId: "",
        toStockCategoryId: "",
      }));
      return;
    }

    const fetchStock = async () => {
      setIsLoadingStock(true);
      try {
        const res = await stockProcessingService.getTransferLotwiseStockCategory({
          lotId: form.fromLotId,
        });
        const items = res.items || [];
        setFromLotwiseStock(items);

        // Pre-fill if only one category is available
        if (items.length === 1 && !form.fromStockCategoryId) {
          setForm((prev) => ({
            ...prev,
            fromStockCategoryId: String(items[0].stockCategoryId),
            toStockCategoryId: String(items[0].stockCategoryId),
          }));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoadingStock(false);
      }
    };
    fetchStock();
  }, [form.fromLotId]);

  const sourceCategoryOptions = useMemo(() => {
    return fromLotwiseStock.map((s) => ({
      label: `${s.stockCategoryName} (Avail: ${Number(s.remainingWeight || 0).toLocaleString()} Kg)`,
      value: String(s.stockCategoryId),
      remainingWeight: s.remainingWeight,
    }));
  }, [fromLotwiseStock]);

  const toLotOptions = useMemo(() => {
    return lotOptions.filter(
      (lot) => String(lot.value) !== String(form.fromLotId),
    );
  }, [lotOptions, form.fromLotId]);

  const lotSummary = useMemo(() => {
    if (!fromLotwiseStock || fromLotwiseStock.length === 0) return null;
    return fromLotwiseStock.reduce(
      (acc, item) => ({
        originalWeight: acc.originalWeight + (parseFloat(item.weight) || 0),
        jointWeight:
          acc.jointWeight + (item.isJoint ? parseFloat(item.weight) || 0 : 0),
        processedWeight:
          acc.processedWeight + (parseFloat(item.processedWeight) || 0),
        remainingWeight:
          acc.remainingWeight + (parseFloat(item.remainingWeight) || 0),
      }),
      {
        originalWeight: 0,
        jointWeight: 0,
        processedWeight: 0,
        remainingWeight: 0,
      },
    );
  }, [fromLotwiseStock]);

  const updateField = (name, value) => {
    setForm((prev) => {
      const nextForm = { ...prev, [name]: value };
      if (name === "fromLotId" && value === prev.toLotId) {
        nextForm.toLotId = "";
      }
      if (name === "fromStockCategoryId") {
        nextForm.toStockCategoryId = value;
      }
      return nextForm;
    });
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleAddNewLot = () => {
    setIsLotModalOpen(true);
  };

  const handleAddNewLotSuccess = async (newLot) => {
    await loadLots();
    if (newLot && (newLot.lotId || newLot.id)) {
      setForm((prev) => ({
        ...prev,
        toLotId: String(newLot.lotId || newLot.id),
      }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!form.fromLotId) errors.fromLotId = "Source Lot is required";
    if (!form.toLotId) errors.toLotId = "Destination Lot is required";
    if (!form.fromStockCategoryId)
      errors.fromStockCategoryId = "Source Category is required";
    if (!form.toStockCategoryId)
      errors.toStockCategoryId = "Destination Category is required";

    if (!form.transferWeight || parseFloat(form.transferWeight) <= 0) {
      errors.transferWeight = "Transfer weight must be greater than 0";
    } else {
      const selectedSource = fromLotwiseStock.find(
        (s) => String(s.stockCategoryId) === String(form.fromStockCategoryId),
      );
      const maxAvailable = selectedSource
        ? parseFloat(selectedSource.remainingWeight) || 0
        : 0;

      if (parseFloat(form.transferWeight) > maxAvailable) {
        errors.transferWeight = `Weight exceeds available stock (${Number(maxAvailable).toLocaleString()} Kg)`;
      }
    }

    if (form.fromLotId && form.toLotId && form.fromLotId === form.toLotId) {
      errors.toLotId = "Source and Destination Lot cannot be the same";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Helper to get max available for UI rendering
  const maxAvailableWeight = useMemo(() => {
    if (!form.fromStockCategoryId || fromLotwiseStock.length === 0) return null;
    const selectedSource = fromLotwiseStock.find(
      (s) => String(s.stockCategoryId) === String(form.fromStockCategoryId),
    );
    return selectedSource
      ? parseFloat(selectedSource.remainingWeight) || 0
      : null;
  }, [form.fromStockCategoryId, fromLotwiseStock]);

  const handleWeightChange = (e) => {
    const val = e.target.value;
    updateField("transferWeight", val);

    // Real-time error clearing or setting
    if (val && parseFloat(val) > 0) {
      if (maxAvailableWeight !== null && parseFloat(val) > maxAvailableWeight) {
        setFieldErrors((prev) => ({
          ...prev,
          transferWeight: `Weight exceeds available stock (${Number(maxAvailableWeight).toLocaleString()} Kg)`,
        }));
      } else {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next.transferWeight;
          return next;
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const fromLotLabel =
      lotOptions.find((l) => String(l.value) === String(form.fromLotId))
        ?.label || "N/A";
    const toLotLabel =
      lotOptions.find((l) => String(l.value) === String(form.toLotId))?.label ||
      "N/A";
    const fromCategoryLabel =
      stockCategoryOptions.find(
        (c) => String(c.value) === String(form.fromStockCategoryId),
      )?.label || "N/A";
    const toCategoryLabel =
      stockCategoryOptions.find(
        (c) => String(c.value) === String(form.toStockCategoryId),
      )?.label || "N/A";

    const confirmHtml = `
      <div class="text-left space-y-2 mt-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
        <div class="flex justify-between">
          <span class="font-medium text-slate-500">From Lot:</span>
          <span class="font-bold text-slate-900 dark:text-white">${fromLotLabel}</span>
        </div>
        <div class="flex justify-between">
          <span class="font-medium text-slate-500">From Category:</span>
          <span class="font-bold text-slate-900 dark:text-white">${fromCategoryLabel}</span>
        </div>
        <div class="border-t border-slate-200 dark:border-slate-700 my-2"></div>
        <div class="flex justify-between">
          <span class="font-medium text-slate-500">To Lot:</span>
          <span class="font-bold text-slate-900 dark:text-white">${toLotLabel}</span>
        </div>
        <div class="flex justify-between">
          <span class="font-medium text-slate-500">To Category:</span>
          <span class="font-bold text-slate-900 dark:text-white">${toCategoryLabel}</span>
        </div>
        <div class="border-t border-slate-200 dark:border-slate-700 my-2"></div>
        <div class="flex justify-between items-center pt-1">
          <span class="font-medium text-slate-500">Transfer Weight:</span>
          <span class="text-lg font-black text-primary-600">${Number(form.transferWeight).toLocaleString()} Kg</span>
        </div>
      </div>
    `;

    const result = await alertService.confirm(confirmHtml, {
      title: "Confirm Stock Transfer",
      confirmButtonText: "Execute Transfer",
    });

    if (!result.isConfirmed) return;

    setIsSubmitting(true);
    setError("");

    try {
      await stockProcessingService.transferStock({
        fromLotId: Number(form.fromLotId),
        toLotId: Number(form.toLotId),
        fromStockCategoryId: Number(form.fromStockCategoryId),
        toStockCategoryId: Number(form.toStockCategoryId),
        transferWeight: Number(form.transferWeight),
      });
      alertService.success("Stock transferred successfully");
      navigate("/transfer-stock");
    } catch (err) {
      setError(err.message || "Failed to transfer stock");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
            <span className="text-primary-600">Stock Transfer</span>
          </h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
            Move inventory between lots and categories
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate("/transfer-stock")}
        >
          <i className="material-icons text-base mr-1">arrow_back</i> Back to
          List
        </Button>
      </div>

      {error && <ErrorAlert message={error} />}

      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Left Side: Form */}
        <div className="w-full flex-1">
          <form
            onSubmit={handleSubmit}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-3 sm:p-4 shadow-sm space-y-4"
          >
            {/* Source Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 pb-2">
                {/* <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 text-[11px] font-black text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">01</span> */}
                <div>
                  <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
                    Source Details
                  </h3>
                  <p className="text-[11px] font-medium text-slate-500">
                    Select where the stock is coming from.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5 bg-slate-50/50 dark:bg-slate-800/20 rounded-2xl border border-slate-100 dark:border-slate-800/50">
                <SearchableDropdown
                  label="From Lot"
                  value={form.fromLotId}
                  options={lotOptions}
                  placeholder="Select Source Lot"
                  onChange={(e) => updateField("fromLotId", e.target.value)}
                  error={fieldErrors.fromLotId}
                />
                <SearchableDropdown
                  label="Category"
                  value={form.fromStockCategoryId}
                  options={sourceCategoryOptions}
                  placeholder={
                    isLoadingStock ? "Loading Stock..." : "Select Category"
                  }
                  disabled={!form.fromLotId || isLoadingStock}
                  onChange={(e) =>
                    updateField("fromStockCategoryId", e.target.value)
                  }
                  error={fieldErrors.fromStockCategoryId}
                />
                <SearchableDropdown
                  label="To Lot"
                  value={form.toLotId}
                  options={toLotOptions}
                  placeholder="Select Target Lot"
                  onChange={(e) => updateField("toLotId", e.target.value)}
                  error={fieldErrors.toLotId}
                  onAdd={handleAddNewLot}
                  addLabel="Add New Lot"
                />
                <Input
                  label="Transfer Weight (Kg)"
                  type="number"
                  value={form.transferWeight}
                  placeholder="0.00"
                  onChange={handleWeightChange}
                  error={fieldErrors.transferWeight}
                  className="font-bold text-lg"
                />
              </div>

              <div className="flex justify-end">
                <div className="flex gap-3 w-full md:w-auto pb-1 justify-end">
                  <Button
                    variant="ghost"
                    onClick={() => navigate("/transfer-stock")}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    className="px-8 shadow-md shadow-primary-500/20"
                  >
                    Execute Transfer
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Right Side: Summary & Available Stock */}
        <div className="w-full lg:w-[400px] xl:w-[450px] shrink-0 space-y-6 lg:sticky lg:top-6">
          {form.fromLotId ? (
            <div className="flex flex-col gap-3 animate-in fade-in duration-300">
              {/* --- Available Stock Summary --- */}
              {fromLotwiseStock.length > 0 && (
                <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400">
                      <i className="material-icons text-lg">inventory_2</i>
                    </div>
                    <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 uppercase tracking-tight">
                      Available Stock
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {fromLotwiseStock.map((item, idx) => (
                      <div
                        key={idx}
                        onClick={() =>
                          updateField(
                            "fromStockCategoryId",
                            String(item.stockCategoryId),
                          )
                        }
                        className={`flex flex-col p-2.5 rounded-xl border transition-all cursor-pointer hover:scale-[1.01] active:scale-95 ${
                          String(form.fromStockCategoryId) ===
                          String(item.stockCategoryId)
                            ? "bg-primary-50 border-primary-500/50 shadow-sm shadow-primary-500/10 dark:bg-primary-900/20 dark:border-primary-500/50"
                            : "bg-slate-50 border-slate-100 hover:border-primary-500/30 dark:bg-slate-800/50 dark:border-slate-700"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-slate-100 dark:border-slate-800">
                          <span
                            className={`text-[10px] font-black uppercase truncate ${
                              String(form.fromStockCategoryId) ===
                              String(item.stockCategoryId)
                                ? "text-primary-700"
                                : "text-slate-700 dark:text-slate-300"
                            }`}
                            title={item.stockCategoryName}
                          >
                            {item.stockCategoryName}
                          </span>
                          {item.isJoint && (
                            <span className="text-[8px] font-black bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded uppercase tracking-tighter">
                              Joint
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                          <div className="flex flex-col">
                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter">
                              Total
                            </span>
                            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400 font-mono">
                              {Number(item.weight || 0).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex flex-col text-right">
                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter">
                              Proc
                            </span>
                            <span className="text-[10px] font-bold text-emerald-600 font-mono">
                              {Number(
                                item.processedWeight || 0,
                              ).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex flex-col col-span-2 mt-0.5 pt-1 border-t border-slate-100/50 dark:border-slate-800/50">
                            <div className="flex justify-between items-end">
                              <span className="text-[8px] font-black text-primary-500 uppercase tracking-tight">
                                Remaining
                              </span>
                              <span className="text-xs font-black text-primary-600 font-mono">
                                {Number(
                                  item.remainingWeight || 0,
                                ).toLocaleString()}{" "}
                                <span className="text-[8px] font-normal text-slate-400">
                                  Kg
                                </span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center dark:border-slate-800 dark:bg-slate-900/50">
              <p className="text-sm font-medium text-slate-500">
                Select a Source Lot to view available stock details.
              </p>
            </div>
          )}
        </div>
      </div>
      <LotCreateModal
        isOpen={isLotModalOpen}
        onClose={() => setIsLotModalOpen(false)}
        onSuccess={handleAddNewLotSuccess}
      />
    </div>
  );
}

export default StockTransferCreatePage;
